import { useCallback, useEffect, useState } from "react";
import {
  AdminTableCard,
  AdminTableState,
  AdminPrimaryButton,
  adminTableHead,
  adminTableCell,
  formatAdminDateTime,
} from "../admin/AdminPageShell";
import { formatSlotRange } from "../../utils/surgeryBookingCalendar";
import api from "../../utils/api";
import { useToast } from "../utilities/Toast";

type RecordRow = {
  id: number;
  lab_token: string;
  lab_name: string;
  lab_email: string;
  slot_date: string;
  slot_hour: number;
  slot_minute?: number;
  ist_label?: string;
  client_region?: string | null;
  client_local_date?: string | null;
  client_local_hour?: number | null;
  client_local_label?: string | null;
  booked_by: string;
  booked_by_role: string;
  status: string;
  surgery_outcome: "pending" | "completed" | "not_completed";
  outcome_marked_at?: string | null;
  outcome_marked_by?: string | null;
  notes_count: number;
  created_at: string;
};

type NoteRow = {
  id: number;
  note: string;
  created_by: string;
  created_by_role?: "client" | "admin";
  created_at: string;
};

type LogRow = {
  id: number;
  action: string;
  detail: string | null;
  actor: string;
  created_at: string;
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "upcoming", label: "Upcoming" },
  { id: "needs_outcome", label: "Needs outcome" },
  { id: "completed", label: "Completed" },
  { id: "not_completed", label: "Not completed" },
  { id: "cancelled", label: "Cancelled" },
] as const;

const outcomeBadge = (outcome: string, status: string) => {
  if (status === "cancelled") {
    return "bg-gray-200 text-gray-600";
  }
  if (outcome === "completed") return "bg-emerald-100 text-emerald-700";
  if (outcome === "not_completed") return "bg-amber-100 text-amber-800";
  return "bg-sky-100 text-sky-800";
};

type Props = { canWrite: boolean; refreshKey?: number };

export default function SurgeryRecordsPanel({ canWrite, refreshKey = 0 }: Props) {
  const { showToast } = useToast();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["id"]>("all");
  const [rows, setRows] = useState<RecordRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<RecordRow | null>(null);
  const [notes, setNotes] = useState<NoteRow[]>([]);
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [markingOutcome, setMarkingOutcome] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/surgery-booking/admin/records", {
        params: { filter },
      });
      setRows(res.data?.data?.bookings || []);
    } catch (err: any) {
      showToast(
        err?.response?.data?.message || "Failed to load surgery records",
        "error",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter, showToast]);

  const openDetail = useCallback(
    async (id: number) => {
      setSelectedId(id);
      setDetailLoading(true);
      setNoteDraft("");
      try {
        const res = await api.get(`/api/surgery-booking/admin/${id}`);
        setDetail(res.data?.data?.booking || null);
        setNotes(res.data?.data?.notes || []);
        setLogs(res.data?.data?.logs || []);
      } catch (err: any) {
        showToast(
          err?.response?.data?.message || "Failed to load booking detail",
          "error",
        );
        setSelectedId(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [showToast],
  );

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords, refreshKey]);

  const addNote = async () => {
    if (!selectedId || !noteDraft.trim() || savingNote) return;
    setSavingNote(true);
    try {
      await api.post(`/api/surgery-booking/admin/${selectedId}/notes`, {
        note: noteDraft.trim(),
      });
      showToast("Note added", "success");
      setNoteDraft("");
      await openDetail(selectedId);
      await fetchRecords();
    } catch (err: any) {
      showToast(err?.response?.data?.message || "Failed to add note", "error");
    } finally {
      setSavingNote(false);
    }
  };

  const markOutcome = async (outcome: "completed" | "not_completed") => {
    if (!selectedId || markingOutcome) return;
    setMarkingOutcome(true);
    try {
      await api.put(`/api/surgery-booking/admin/${selectedId}/outcome`, {
        outcome,
      });
      showToast(
        outcome === "completed"
          ? "Marked surgery completed"
          : "Marked surgery not completed",
        "success",
      );
      await openDetail(selectedId);
      await fetchRecords();
    } catch (err: any) {
      showToast(
        err?.response?.data?.message || "Failed to update outcome",
        "error",
      );
    } finally {
      setMarkingOutcome(false);
    }
  };

  const cancelBooking = async () => {
    if (!selectedId || cancelling) return;
    if (!window.confirm("Cancel this surgery booking?")) return;
    setCancelling(true);
    try {
      await api.delete(`/api/surgery-booking/admin/${selectedId}`);
      showToast("Booking cancelled", "success");
      setSelectedId(null);
      setDetail(null);
      await fetchRecords();
    } catch (err: any) {
      showToast(
        err?.response?.data?.message || "Failed to cancel booking",
        "error",
      );
    } finally {
      setCancelling(false);
    }
  };

  const colSpan = canWrite ? 8 : 7;

  return (
    <>
      <AdminTableCard>
        <div className="px-5 py-4 border-b border-[#F0F0EC] flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-[#4A4A48]">
              Surgery records & logs
            </p>
            <p className="text-xs text-[#8A8A85]">
              Full history of bookings, notes, and completion status
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={[
                  "rounded-full px-3 py-1 text-xs font-medium border transition",
                  filter === f.id
                    ? "bg-[#2fa9bf] border-[#2fa9bf] text-white"
                    : "bg-white border-[#DDDDD8] text-[#4A4A48] hover:bg-[#F8F8F6]",
                ].join(" ")}
              >
                {f.label}
              </button>
            ))}
            <button
              type="button"
              onClick={fetchRecords}
              className="text-xs font-medium text-[#2fa9bf] hover:underline ml-1"
            >
              Refresh
            </button>
          </div>
        </div>

        <table className="table-auto w-full">
          <thead>
            <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
              <th className={adminTableHead}>IST slot</th>
              <th className={adminTableHead}>Client local</th>
              <th className={adminTableHead}>Lab</th>
              <th className={adminTableHead}>Booked by</th>
              <th className={adminTableHead}>Outcome</th>
              <th className={adminTableHead}>Notes</th>
              <th className={adminTableHead}>Created</th>
              {canWrite ? <th className={adminTableHead}>Open</th> : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <AdminTableState
                colSpan={colSpan}
                variant="loading"
                message="Loading records…"
              />
            ) : rows.length === 0 ? (
              <AdminTableState
                colSpan={colSpan}
                variant="empty"
                message="No surgery records"
              />
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-[#F0F0EC] last:border-0 hover:bg-[#FAFAF8] cursor-pointer"
                  onClick={() => openDetail(row.id)}
                >
                  <td className={adminTableCell}>
                    <span className="font-semibold">
                      {row.ist_label ||
                        `${row.slot_date} ${formatSlotRange(row.slot_hour)} IST`}
                    </span>
                    {row.status === "cancelled" ? (
                      <span className="ml-2 text-[10px] uppercase text-red-500">
                        cancelled
                      </span>
                    ) : null}
                  </td>
                  <td className={adminTableCell}>
                    {row.client_local_hour != null && row.client_local_date
                      ? `${row.client_local_date} ${formatSlotRange(row.client_local_hour)}${
                          row.client_region ? ` (${row.client_region})` : ""
                        }`
                      : row.client_local_label || "—"}
                  </td>
                  <td className={adminTableCell}>
                    <div className="flex flex-col">
                      <span className="font-semibold">{row.lab_token}</span>
                      <span className="text-[11px] text-[#8A8A85]">
                        {row.lab_name}
                      </span>
                    </div>
                  </td>
                  <td className={adminTableCell}>
                    <div className="flex flex-col">
                      <span>{row.booked_by}</span>
                      <span className="text-[11px] text-[#8A8A85] capitalize">
                        {row.booked_by_role}
                      </span>
                    </div>
                  </td>
                  <td className={adminTableCell}>
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${outcomeBadge(
                        row.surgery_outcome,
                        row.status,
                      )}`}
                    >
                      {row.status === "cancelled"
                        ? "cancelled"
                        : row.surgery_outcome.replace("_", " ")}
                    </span>
                  </td>
                  <td className={adminTableCell}>{row.notes_count}</td>
                  <td className={adminTableCell}>
                    {formatAdminDateTime(row.created_at)}
                  </td>
                  {canWrite ? (
                    <td className={adminTableCell}>
                      <button
                        type="button"
                        className="text-xs font-medium text-[#2fa9bf] hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetail(row.id);
                        }}
                      >
                        Manage
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </AdminTableCard>

      {selectedId != null ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-[76.8rem] max-h-[90vh] overflow-hidden border border-[#E8E8E4] flex flex-col">
            <div className="px-5 py-4 border-b border-[#F0F0EC] flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-[#4A4A48]">
                  Surgery booking #{selectedId}
                </h2>
                {detail ? (
                  <p className="text-xs text-[#8A8A85] mt-1">
                    {detail.lab_token} ·{" "}
                    {detail.ist_label ||
                      `${detail.slot_date} ${formatSlotRange(detail.slot_hour)} IST`}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedId(null);
                  setDetail(null);
                }}
                className="text-xs font-medium text-[#8A8A85] hover:text-[#4A4A48]"
              >
                Close
              </button>
            </div>

            <div className="overflow-y-auto px-5 py-4">
              {detailLoading || !detail ? (
                <p className="text-sm text-[#8A8A85]">Loading…</p>
              ) : (
                <div className="flex flex-col md:flex-row gap-5 text-sm">
                  <div className="flex-1 min-w-0 space-y-5">
                    <div className="rounded-xl border border-[#E8E8E4] bg-[#FAFAF8] p-3">
                      <p className="text-[11px] uppercase tracking-wide text-[#8A8A85]">
                        Client
                      </p>
                      <p className="font-semibold text-[#4A4A48]">
                        {detail.lab_name} ({detail.lab_token})
                      </p>
                      <p className="text-xs text-[#8A8A85]">{detail.lab_email}</p>
                      <p className="text-xs text-[#8A8A85] mt-1">
                        Local:{" "}
                        {detail.client_local_label ||
                          (detail.client_local_date &&
                          detail.client_local_hour != null
                            ? `${detail.client_local_date} ${formatSlotRange(detail.client_local_hour)}`
                            : "—")}
                      </p>
                    </div>

                    <div className="rounded-xl border border-[#E8E8E4] p-3 space-y-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#8A8A85]">
                        Surgery notes
                      </p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {notes.length === 0 ? (
                          <p className="text-xs text-[#8A8A85]">No notes yet</p>
                        ) : (
                          notes.map((n) => (
                            <div
                              key={n.id}
                              className="rounded-lg bg-[#F8F8F6] px-3 py-2 text-xs text-[#4A4A48]"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span
                                  className={[
                                    "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                                    n.created_by_role === "client"
                                      ? "bg-emerald-100 text-emerald-700"
                                      : "bg-slate-200 text-slate-700",
                                  ].join(" ")}
                                >
                                  {n.created_by_role === "client"
                                    ? "Client"
                                    : "Admin"}
                                </span>
                              </div>
                              <p className="whitespace-pre-wrap">{n.note}</p>
                              <p className="text-[10px] text-[#8A8A85] mt-1">
                                {n.created_by} ·{" "}
                                {formatAdminDateTime(n.created_at)}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                      {canWrite ? (
                        <div className="space-y-2">
                          <textarea
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                            rows={3}
                            placeholder="Add a note useful at surgery time…"
                            className="w-full rounded-xl border border-[#DDDDD8] bg-white px-3 py-2 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20"
                          />
                          <AdminPrimaryButton
                            disabled={savingNote || !noteDraft.trim()}
                            onClick={addNote}
                          >
                            {savingNote ? "Saving…" : "Add note"}
                          </AdminPrimaryButton>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0 space-y-5">
                    <div className="rounded-xl border border-[#E8E8E4] bg-[#FAFAF8] p-3">
                      <p className="text-[11px] uppercase tracking-wide text-[#8A8A85]">
                        Status
                      </p>
                      <p className="font-semibold text-[#4A4A48] capitalize">
                        Booking: {detail.status}
                      </p>
                      <p className="text-xs text-[#8A8A85] capitalize mt-1">
                        Outcome: {detail.surgery_outcome.replace("_", " ")}
                        {detail.outcome_marked_by
                          ? ` · by ${detail.outcome_marked_by}`
                          : ""}
                      </p>
                      {detail.outcome_marked_at ? (
                        <p className="text-[11px] text-[#8A8A85]">
                          Marked {formatAdminDateTime(detail.outcome_marked_at)}
                        </p>
                      ) : null}
                      <p className="text-[11px] text-[#8A8A85] mt-1">
                        Booked by {detail.booked_by} ({detail.booked_by_role}) ·{" "}
                        {formatAdminDateTime(detail.created_at)}
                      </p>
                    </div>

                    {canWrite && detail.status === "active" ? (
                      <div className="rounded-xl border border-[#E8E8E4] p-3 space-y-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-[#8A8A85]">
                          After surgery — mark outcome
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <AdminPrimaryButton
                            disabled={markingOutcome}
                            onClick={() => markOutcome("completed")}
                          >
                            Surgery completed
                          </AdminPrimaryButton>
                          <button
                            type="button"
                            disabled={markingOutcome}
                            onClick={() => markOutcome("not_completed")}
                            className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-xs font-medium border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] disabled:opacity-50"
                          >
                            Not completed
                          </button>
                          <button
                            type="button"
                            disabled={cancelling}
                            onClick={cancelBooking}
                            className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                          >
                            Cancel booking
                          </button>
                        </div>
                      </div>
                    ) : null}

                    <div className="rounded-xl border border-[#E8E8E4] p-3 space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#8A8A85]">
                        Activity log
                      </p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {logs.length === 0 ? (
                          <p className="text-xs text-[#8A8A85]">No log entries</p>
                        ) : (
                          logs.map((l) => (
                            <div
                              key={l.id}
                              className="border-l-2 border-[#2fa9bf] pl-3 py-1"
                            >
                              <p className="text-xs font-semibold text-[#4A4A48] capitalize">
                                {l.action.replace(/_/g, " ")}
                              </p>
                              {l.detail ? (
                                <p className="text-[11px] text-[#4A4A48] whitespace-pre-wrap">
                                  {l.detail}
                                </p>
                              ) : null}
                              <p className="text-[10px] text-[#8A8A85]">
                                {l.actor} · {formatAdminDateTime(l.created_at)}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
